import { useEffect, useRef, useState } from "react";
import { Bold, Italic, List, ListOrdered, Heading2, Heading3, Quote, Link as LinkIcon } from "lucide-react";

/**
 * Lightweight rich-text editor that accepts pasted content from Google Docs /
 * Word and preserves basic formatting (bold, italic, headings, lists, links).
 *
 * Design intentionally minimal:
 *   - Uses `contenteditable` + `document.execCommand` for the toolbar.
 *   - On paste, we let the browser drop styled HTML into the editor, then
 *     strip all `style=` / `class=` attributes and any tags the server won't
 *     allow. Final sanitization happens server-side via `bleach.clean`.
 */

const ALLOWED_TAGS = new Set([
  "P", "BR", "HR",
  "STRONG", "B", "EM", "I", "U", "S",
  "UL", "OL", "LI",
  "BLOCKQUOTE",
  "H2", "H3", "H4",
  "A", "SPAN",
]);

function sanitizeNode(node) {
  // Recursively unwrap disallowed elements and strip inline styles/classes so
  // Google Docs pastes don't drag in giant color palettes and font families.
  const kids = Array.from(node.children);
  kids.forEach((child) => {
    if (!ALLOWED_TAGS.has(child.tagName)) {
      // Unwrap: move children up, then remove.
      while (child.firstChild) node.insertBefore(child.firstChild, child);
      child.remove();
      return;
    }
    child.removeAttribute("style");
    child.removeAttribute("class");
    child.removeAttribute("id");
    // For links, keep href only and force safe rel/target.
    if (child.tagName === "A") {
      const href = child.getAttribute("href");
      Array.from(child.attributes).forEach((a) => child.removeAttribute(a.name));
      if (href && (href.startsWith("http") || href.startsWith("mailto:"))) {
        child.setAttribute("href", href);
        child.setAttribute("target", "_blank");
        child.setAttribute("rel", "noopener noreferrer");
      }
    }
    sanitizeNode(child);
  });
}

export default function RichTextEditor({ value, onChange, placeholder = "Write or paste from Google Docs…", minHeight = 240, testid = "rich-editor" }) {
  const ref = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  // Only push external value into the DOM when it actually differs. Otherwise
  // React would clobber the caret on every keystroke.
  useEffect(() => {
    if (!ref.current) return;
    if (ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
    // eslint-disable-next-line
  }, []);

  const emit = () => {
    if (!ref.current) return;
    onChange(ref.current.innerHTML);
  };

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    ref.current?.focus();
    emit();
  };

  const insertLink = () => {
    const url = window.prompt("Link URL:");
    if (url) exec("createLink", url);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");
    if (html) {
      const container = document.createElement("div");
      container.innerHTML = html;
      sanitizeNode(container);
      document.execCommand("insertHTML", false, container.innerHTML);
    } else {
      document.execCommand("insertText", false, text);
    }
    emit();
  };

  return (
    <div className="rounded-md" style={{ border: `1px solid ${isFocused ? "var(--ember)" : "var(--border-subtle)"}`, background: "var(--surface)" }} data-testid={testid}>
      <div className="flex items-center gap-1 px-2 py-1.5 border-b" style={{ borderColor: "var(--border-subtle)" }}>
        {[
          { cmd: "bold", icon: Bold, label: "Bold" },
          { cmd: "italic", icon: Italic, label: "Italic" },
          { cmd: "formatBlock:H2", icon: Heading2, label: "Heading 2" },
          { cmd: "formatBlock:H3", icon: Heading3, label: "Heading 3" },
          { cmd: "formatBlock:BLOCKQUOTE", icon: Quote, label: "Quote" },
          { cmd: "insertUnorderedList", icon: List, label: "Bulleted list" },
          { cmd: "insertOrderedList", icon: ListOrdered, label: "Numbered list" },
        ].map(({ cmd, icon: Icon, label }) => (
          <button
            key={cmd}
            type="button"
            title={label}
            onMouseDown={(e) => { e.preventDefault(); }}
            onClick={() => {
              if (cmd.startsWith("formatBlock:")) exec("formatBlock", cmd.split(":")[1]);
              else exec(cmd);
            }}
            className="p-1.5 rounded hover:opacity-80 transition-opacity"
            style={{ color: "var(--ink-2)" }}
            data-testid={`${testid}-${cmd.replace(":", "-").toLowerCase()}`}
          >
            <Icon size={14} />
          </button>
        ))}
        <button
          type="button"
          title="Link"
          onMouseDown={(e) => e.preventDefault()}
          onClick={insertLink}
          className="p-1.5 rounded hover:opacity-80 transition-opacity"
          style={{ color: "var(--ink-2)" }}
          data-testid={`${testid}-link`}
        >
          <LinkIcon size={14} />
        </button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onPaste={handlePaste}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className="px-3 py-2 text-sm focus:outline-none rich-editor-content"
        style={{ minHeight, color: "var(--ink)", lineHeight: 1.6 }}
        data-testid={`${testid}-content`}
        aria-label={placeholder}
      />
    </div>
  );
}
